class RebuyCart {
  constructor({
    host,
    apiKey,
    datasourceId,
    widgetId,
    saleBadgeText,
    attribution,
    source,
    currencySymbol,
    currencyPosition,
    currencySpace,
    decimalSeparator,
    thousandsSeparator,
    decimals,
    showPercentageSale,
  }) {
    this.host = host;
    this.apiKey = apiKey;
    this.datasourceId = datasourceId;
    this.widgetId = widgetId;
    this.saleBadgeText = saleBadgeText || 'Sale';
    this.attribution = attribution || 'Rebuy Cart';
    this.source = source || 'Rebuy';
    this.currencySymbol = this.normalizeCurrencySymbol(currencySymbol || '€');
    this.currencyPosition = currencyPosition || 'before';
    this.currencySpace = currencySpace === 'true';
    this.decimalSeparator = decimalSeparator || ',';
    this.thousandsSeparator = thousandsSeparator || ' ';
    this.decimals = parseInt(decimals, 10) || 2;
    this.showPercentageSale = showPercentageSale === 'true';
    this.cart = document.querySelector('slide-cart');
    this.apiResponse = null;
  }

  async init() {
    if (!this.host) return;

    try {
      this.clearProducts();
      this.host.classList.add('sc-rebuy--loading');

      const cartData = this.getCartData();

      if (!cartData) {
        this.hide();
        return;
      }

      this.show();

      const data = await this.fetchRecommendations(cartData);

      if (!data.data || data.data.length === 0) {
        this.hide();
        return;
      }

      this.apiResponse = data;
      const products = data.data.slice(0, 3);
      await this.enrichWithOmnibusPrices(products);
      this.render(products);
    } catch (err) {
      console.error('RebuyCart error:', err);
      this.hide();
    } finally {
      this.host.classList.remove('sc-rebuy--loading');
    }
  }

  hide() {
    this.host.hidden = true;
    this.clearProducts();
  }

  show() {
    this.host.hidden = false;
  }

  clearProducts() {
    const existing = this.host.querySelector('.sc-rebuy__products');
    if (existing) existing.remove();
  }

  getCartData() {
    const el = document.getElementById('SlideCart-Rebuy-CartData');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('RebuyCart: Failed to parse cart data', e);
      return null;
    }
  }

  async fetchRecommendations(cartData) {
    const items = cartData.items;

    const params = new URLSearchParams({
      key: this.apiKey,
      prouct_groups: 'yes',
      bust_cache: 'yes',
      shopify_product_ids: items.map((item) => item.product_id).join(','),
      shopify_variant_ids: items.map((item) => item.variant_id).join(','),
      limit: '3',
      cart_subtotal: String(cartData.total_price),
      cart_item_count: String(cartData.item_count),
      cart_line_count: String(items.length),
    });

    items.forEach((item, i) => {
      params.append(`cart[items][${i}][product_id]`, item.product_id);
      params.append(`cart[items][${i}][variant_id]`, item.variant_id);
      params.append(`cart[items][${i}][quantity]`, item.quantity);
    });

    if (cartData.token) {
      params.set('cart_token', cartData.token);
    }

    const url = `https://rebuyengine.com/api/v1/custom/id/${this.datasourceId}?${params}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Rebuy API error ${res.status}`);
    return res.json();
  }

  async enrichWithOmnibusPrices(products) {
    const shp = window.Shopify;
    if (!shp) return;

    const fetches = [];

    products.forEach((product) => {
      (product.variants || []).forEach((variant) => {
        fetches.push(
          fetch('/apps/am-omnibus-price', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              shop: shp.shop,
              productId: product.id,
              variantId: variant.id,
              locale: shp.locale,
              currency: shp.currency,
            }),
          })
            .then((res) => res.json())
            .then((data) => {
              if (data?.lowestPriceHtml) {
                variant._omnibusHtml = data.lowestPriceHtml;
              }
            })
            .catch((err) => console.error('RebuyCart omnibus error:', err))
        );
      });
    });

    await Promise.all(fetches);
  }

  render(products) {
    const cards = products.map((p) => this.renderCard(p)).join('');

    // Remove previous products if re-rendering
    const existing = this.host.querySelector('.sc-rebuy__products');
    if (existing) existing.remove();

    this.host.insertAdjacentHTML('beforeend', `<div class="sc-rebuy__products">${cards}</div>`);
    this.bindEvents();
  }

  renderCard(product) {
    const variant = product.variants?.[0] || {};
    const imgSrc = product.image?.src || product.images?.[0]?.src || '';
    const imgAlt = product.image?.alt || product.title || '';
    const price = variant.price ? this.formatPrice(variant.price) : '';
    const hasCompare = variant.compare_at_price && variant.compare_at_price !== variant.price;
    const compareAt = hasCompare ? this.formatPrice(variant.compare_at_price) : '';
    const saleBadgeLabel = hasCompare ? this.getSaleBadgeLabel(variant.compare_at_price, variant.price) : '';
    const link = `${product.link}?variant=${variant.id}`;

    const variantSelectHtml =
      product.variants.length > 1
        ? `<div class="sc-rebuy__variant-wrap">
          <select class="sc-rebuy__variant-select" data-product-id="${product.id}">
            ${product.variants.map((v) => `<option value="${v.id}">${v.title}</option>`).join('')}
          </select>
        </div>`
        : `<input type="hidden" class="sc-rebuy__variant-select" data-product-id="${product.id}" value="${variant.id}">`;

    return `
      <div class="sc-rebuy__product" data-product-id="${product.id}">
        <a href="${link}" class="sc-rebuy__product-link">
          <div class="sc-rebuy__product-image">
            <img src="${imgSrc}" alt="${imgAlt}" loading="lazy" width="80">
          </div>
        </a>
        <div class="sc-rebuy__product-info">
          <a href="${link}" class="sc-rebuy__product-title">${product.title}</a>
          ${variantSelectHtml}
          <div class="sc-rebuy__product-pricing">
            ${compareAt ? `<span class="badge badge--sale">${saleBadgeLabel}</span>` : ''}
            ${compareAt ? `<s class="sc-rebuy__compare">${compareAt}</s>` : ''}
            <span class="sc-rebuy__price">${price}</span>
          </div>
          ${variant._omnibusHtml ? `<div class="sc-rebuy__omnibus">${variant._omnibusHtml}</div>` : ''}
        </div>
        <button class="sc-rebuy__add-btn" data-product-id="${product.id}" type="button" aria-label="Add to cart">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="icon-cart">
            <path d="M8.65332 17.9424C9.46239 17.9424 10.1181 18.6265 10.1182 19.4707C10.1182 20.315 9.46243 21 8.65332 21C7.84427 20.9999 7.18848 20.3149 7.18848 19.4707C7.18855 18.6265 7.84432 17.9425 8.65332 17.9424ZM18.9092 17.9424C19.7182 17.9424 20.3739 18.6265 20.374 19.4707C20.374 20.315 19.7183 21 18.9092 21C18.1001 20.9999 17.4443 20.315 17.4443 19.4707C17.4444 18.6265 18.1002 17.9424 18.9092 17.9424ZM5.72363 2.40039C6.19732 2.40063 6.60219 2.7554 6.68457 3.24219L6.92969 4.69336H20.6582V4.69434C20.8068 4.69436 20.9548 4.71843 21.0947 4.76855L21.2314 4.82715L21.3623 4.90137C21.4466 4.95555 21.5248 5.01964 21.5957 5.0918L21.6963 5.20605L21.7842 5.33105C21.838 5.41749 21.8826 5.50981 21.917 5.60645L21.9609 5.75488L21.9893 5.90723C22.0025 6.00947 22.0037 6.11305 21.9951 6.21582L21.9746 6.36914L20.6562 13.248C20.5953 13.5656 20.4313 13.8523 20.1914 14.0576C19.9513 14.2629 19.6493 14.3741 19.3389 14.374V14.375H8.56738L8.74023 15.3945H19.6406C20.1799 15.3945 20.617 15.8504 20.6172 16.4131C20.6172 16.9759 20.18 17.4326 19.6406 17.4326H7.9209C7.44707 17.4326 7.04146 17.0777 6.95898 16.5908L4.9043 4.43848H2.79297C2.25362 4.4384 1.81641 3.98176 1.81641 3.41895C1.81658 2.85629 2.25373 2.40047 2.79297 2.40039H5.72363ZM8.22266 12.3369H18.8398L19.9131 6.73145H7.27441L8.22266 12.3369Z" fill="currentColor"/>
          </svg>
          <div class="loading__spinner">
            <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </div>
        </button>
      </div>
    `;
  }

  getSaleBadgeLabel(compareAtPrice, price) {
    let label = this.saleBadgeText;
    if (this.showPercentageSale) {
      const pct = Math.round((1 - parseFloat(price) / parseFloat(compareAtPrice)) * 100);
      label += ` -${pct}%`;
    }
    return label;
  }

  formatPrice(value) {
    value = parseFloat(value);
    const fixed = value.toFixed(this.decimals);
    let [integer, decimal] = fixed.split('.');
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator);
    const formatted = decimal ? `${integer}${this.decimalSeparator}${decimal}` : integer;
    const space = this.currencySpace ? ' ' : '';
    return this.currencyPosition === 'before'
      ? `${this.currencySymbol}${space}${formatted}`
      : `${formatted} ${this.currencySymbol}`;
  }

  bindEvents() {
    this.host.querySelectorAll('.sc-rebuy__add-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart(btn);
      });
    });

    this.host.querySelectorAll('.sc-rebuy__variant-select').forEach((select) => {
      select.addEventListener('change', () => this.onVariantChange(select));
    });
  }

  async addToCart(btn) {
    const productId = btn.dataset.productId;
    const select = this.host.querySelector(`.sc-rebuy__variant-select[data-product-id="${productId}"]`);
    const variantId = select?.value;
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add('loading');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          sections: this.cart?.getSectionsToRender().map((s) => s.id),
          quantity: 1,
          properties: {
            _source: this.source,
            _attribution: this.attribution,
            _widget_id: this.widgetId,
          },
        }),
      });
      const data = await res.json();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rebuy-cart', cartAction: 'add', cartData: data });
      }
    } catch (err) {
      console.error('RebuyCart add-to-cart error:', err);
    } finally {
      btn.disabled = false;
      btn.classList.remove('loading');
    }
  }

  onVariantChange(selectEl) {
    const productId = selectEl.dataset.productId;
    const variantId = selectEl.value;

    const product = this.apiResponse?.data?.find((p) => String(p.id) === String(productId));
    if (!product) return;

    const variant = product.variants?.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    const card = selectEl.closest('.sc-rebuy__product');
    if (!card) return;

    const priceEl = card.querySelector('.sc-rebuy__price');
    const compareEl = card.querySelector('.sc-rebuy__compare');

    if (priceEl && variant.price) {
      priceEl.textContent = this.formatPrice(variant.price);
    }

    const saleBadge = card.querySelector('.badge.badge--sale');
    const hasCompare = variant.compare_at_price && variant.compare_at_price !== variant.price;

    if (compareEl) {
      if (hasCompare) {
        compareEl.textContent = this.formatPrice(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (saleBadge) {
      saleBadge.style.display = hasCompare ? '' : 'none';
      if (hasCompare) {
        saleBadge.textContent = this.getSaleBadgeLabel(variant.compare_at_price, variant.price);
      }
    }

    const imgEl = card.querySelector('.sc-rebuy__product-image img');
    if (variant.image_id && imgEl) {
      const variantImg = product.images?.find((img) => String(img.id) === String(variant.image_id));
      if (variantImg) imgEl.src = variantImg.src;
    }

    const linkEls = card.querySelectorAll('.sc-rebuy__product-link, .sc-rebuy__product-title');
    linkEls.forEach((el) => {
      if (el.href) {
        el.href = `${product.link}?variant=${variantId}`;
      }
    });

    const omnibusEl = card.querySelector('.sc-rebuy__omnibus');
    if (variant._omnibusHtml) {
      if (omnibusEl) {
        omnibusEl.innerHTML = variant._omnibusHtml;
      } else {
        const pricingEl = card.querySelector('.sc-rebuy__product-pricing');
        if (pricingEl)
          pricingEl.insertAdjacentHTML('afterend', `<div class="sc-rebuy__omnibus">${variant._omnibusHtml}</div>`);
      }
    } else if (omnibusEl) {
      omnibusEl.remove();
    }
  }

  normalizeCurrencySymbol(symbol) {
    const map = {
      'РСД': 'RSD',
      'дин.': 'din.',
    };
    return map[symbol] || symbol;
  }
}

function refreshSlideCartRebuy() {
  const host = document.getElementById('SlideCart-Rebuy');
  if (!host) return;

  const rebuy = new RebuyCart({
    host,
    apiKey: host.dataset.rebuyApiKey,
    datasourceId: host.dataset.rebuyDatasourceId,
    widgetId: host.dataset.rebuyWidgetId,
    saleBadgeText: host.dataset.saleBadgeText,
    attribution: host.dataset.rebuyAttribution,
    source: host.dataset.rebuySource,
    currencySymbol: host.dataset.currencySymbol,
    currencyPosition: host.dataset.currencyPosition,
    currencySpace: host.dataset.currencySpace,
    decimalSeparator: host.dataset.decimalSeparator,
    thousandsSeparator: host.dataset.thousandsSeparator,
    decimals: host.dataset.decimals,
    showPercentageSale: host.dataset.showPercentageSale,
  });

  rebuy.init();
}

document.addEventListener('DOMContentLoaded', refreshSlideCartRebuy);
window.refreshSlideCartRebuy = refreshSlideCartRebuy;
